import React, { useEffect, useRef, useState } from 'react'
import Markdown from '../../components/Markdown'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, LinearProgress, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { Project } from '../../types'
import JournalTab from './JournalTab'
import StudyModeFork from '../../components/datascience/StudyModeFork'
import JudgeReadinessPanel, { JudgeReadinessResult } from '../../components/student/JudgeReadinessPanel'
import { readFunctionError } from '../../lib/functionError'

// Step 4 — Formulate Hypothesis (Scientific Method track). Turns the Step 2
// draft variables + Step 3 research into a testable experiment: variables are
// operationalized, measurement validated, and a falsifiable hypothesis written.
// Engineering-Design track lands later. Mirrors the Step 2/3 per-field
// AI-feedback pattern (step4-evaluate-field → {shape,anchor}).

type Track  = 'scientific_method' | 'engineering_design'
type Status = 'draft' | 'submitted' | 'approved_draft' | 'completed'

interface Dim   { state: 'ok' | 'caution' | 'thinking'; note: string }
// forms (dv group only): SRC safety-review flag. `triggered` names which
// pre-approval categories the study involves; keep/adjust are mentor-voice
// guidance (stay with the live study, or pivot — lead option is a computational
// study on an existing dataset that needs no pre-approval). Informational, never
// a gate on our platform — the fair's SRC enforces.
type FormKey = 'human' | 'vertebrate' | 'biological' | 'tissue' | 'hazard'
interface FormsFlag { triggered: FormKey[]; note?: string; keep?: string; adjust?: string }
// needs_background (hypothesis group only): true when the Step 3 research +
// any supporting background don't yet justify the rationale → reveal the box.
interface AIEval { shape: Dim; anchor: Dim; ai_disclaimer?: string; needs_background?: boolean; forms?: FormsFlag }

// Feedback is now per CONCEPT (group), not per text field. Shared groups key by
// name ('question','iv','cv'); per-block groups key `${group}:${index}`.
type Group =
  | 'question' | 'iv' | 'cv' | 'dv' | 'tool' | 'hypothesis'         // scientific
  | 'problem' | 'audience' | 'prior_art' | 'requirement' | 'solution' // engineering
const BLOCK_GROUPS: Group[] = ['dv', 'tool', 'hypothesis', 'requirement']

// One hypothesis: a dependent variable, how it's measured (and whether a tool
// must be built), plus the hypothesis statement + its grounded reasoning.
// A DV is either measured DIRECTLY (dv_measurement) or DERIVED — calculated from
// measured_quantities via a formula (e.g. force = mass × acceleration).
interface Block {
  dv?:                    string
  dv_kind?:               'direct' | 'derived'
  dv_measurement?:        string   // direct: method + units
  measured_quantities?:   string   // derived: what you actually measure, with units
  formula?:               string   // derived: how the DV is calculated
  needs_tool?:            boolean
  tool?:                  string
  hypothesis?:            string
  rationale?:             string
  supporting_background?: string
}

// Data-science track: the model is the apparatus. Simple technical capture —
// the model itself is built/run in the AI Research Lab, not here.
interface MlPrototype {
  dataset?:    string
  model_name?: string
  package?:    string
  colab_url?:  string
}

interface Payload {
  question?:    string
  iv?:          string   // IV group: definition + how it's manipulated + levels
  cv?:          string   // CV group: what's held constant + why
  blocks?:      Block[]
  ml_prototype?: MlPrototype   // data-science track only
  ai_feedback?: Record<string, AIEval>
  judge_readiness?: JudgeReadinessResult   // last judge-readiness (winning-bar) result

  // ── legacy single-field keys (pre-block); read only for migration ──
  iv_type?:         'continuous' | 'categorical'
  trials?:          string
  dv?:              string
  dv_measurement?:  string
  needs_apparatus?: boolean
  apparatus?:       string
  hypothesis?:      string
  justification?:   string
}

const emptyBlock = (): Block => ({
  dv: '', dv_kind: 'direct', dv_measurement: '', measured_quantities: '', formula: '',
  needs_tool: false, tool: '',
  hypothesis: '', rationale: '', supporting_background: '',
})

// Plain-English recap of the two "how you get a number" choices (direct/derived
// × standard-tool/build), so the combination is visible and self-checking.
function dvSummary(b: Block): string {
  const dv = b.dv?.trim() || 'it'
  if (b.dv_kind === 'derived') {
    const meas = b.measured_quantities?.trim() || 'your measurements'
    return `You'll measure ${meas} with ${b.needs_tool ? "tools you'll build" : 'standard tools'}, then calculate ${dv}.`
  }
  return `You'll measure ${dv} directly with ${b.needs_tool ? "a tool you'll build" : 'a standard tool'}.`
}

// Fold a saved payload into the block shape. Old single-field payloads (and the
// Step 2 carry-forward seed) become blocks[0]; IV definition + old trial setup
// merge into the single IV field. Group feedback keys changed, so stale
// per-field feedback is dropped (only 'question' survives unchanged).
function toBlockPayload(saved: Payload | null, seed2: { question?: string; iv?: string; dv?: string }): Payload {
  if (saved && Array.isArray(saved.blocks)) return saved
  if (!saved) {
    return { question: seed2.question ?? '', iv: seed2.iv ?? '', cv: '', blocks: [{ ...emptyBlock(), dv: seed2.dv ?? '' }], ai_feedback: {} }
  }
  const iv = [saved.iv, saved.trials].map(s => (s ?? '').trim()).filter(Boolean).join('\n\n')
  const block: Block = {
    ...emptyBlock(),
    dv:             saved.dv ?? '',
    dv_measurement: saved.dv_measurement ?? '',
    needs_tool:     saved.needs_apparatus ?? false,
    tool:           saved.apparatus ?? '',
    hypothesis:     saved.hypothesis ?? '',
    rationale:      saved.justification ?? '',
  }
  const keptFeedback: Record<string, AIEval> = {}
  if (saved.ai_feedback?.question) keptFeedback.question = saved.ai_feedback.question
  return { question: saved.question ?? '', iv, cv: '', blocks: [block], ai_feedback: keptFeedback }
}

// ── Engineering Design track ────────────────────────────────────────────────
// A requirement paired with its performance criterion (how it's validated) —
// the engineering parallel to the scientific hypothesis ↔ rationale pairing.
interface EngBlock { requirement?: string; criterion?: string }

interface EngPayload {
  problem?:          string   // the problem to solve (carried from Step 2)
  for_human_user?:   boolean  // default true — most eng projects have human users
  users_needs?:      string   // human-centered: target users + why + their 1–3 needs
  performance_goal?: string   // performance-centered (for_human_user === false): beneficiary + goal
  prior_art?:        string   // 3 existing solutions + 3 gaps (grounded in Step 3 research)
  requirements?:     EngBlock[] // repeatable requirement+criterion pairs (1 primary, up to 5)
  mvp?:              string   // Solution tab
  tools?:            string
  solution?:         string
  ai_feedback?:      Record<string, AIEval>
  judge_readiness?:  JudgeReadinessResult   // last judge-readiness (winning-bar) result
}

const emptyEngBlock = (): EngBlock => ({ requirement: '', criterion: '' })

// Fold a saved eng payload into shape, carrying the Step 2 problem/user forward
// on first entry and guaranteeing at least one requirement block.
function toEngPayload(saved: EngPayload | null, seed2: { problem?: string; users_needs?: string }): EngPayload {
  const base = saved ?? {}
  return {
    ...base,
    problem:        base.problem ?? seed2.problem ?? '',
    for_human_user: base.for_human_user ?? true,
    users_needs:    base.users_needs ?? seed2.users_needs ?? '',
    requirements:   base.requirements?.length ? base.requirements : [emptyEngBlock()],
    ai_feedback:    base.ai_feedback ?? {},
  }
}

export default function Step4Page() {
  const { session } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, loading: vasLoading } = useViewerStudent()
  const navigate = useNavigate()

  const [project, setProject] = useState<Project | null>(null)
  const [track,   setTrack]   = useState<Track>('scientific_method')
  const [topic,   setTopic]   = useState<string>('')
  const [purpose, setPurpose] = useState<string>('')
  const [status,  setStatus]  = useState<Status>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [sectionComments, setSectionComments] = useState<Record<string, string>>({})
  const [seed,    setSeed]    = useState<Payload | EngPayload | null>(null)
  const [background, setBackground] = useState<Record<string, any>>({})
  const [grade,   setGrade]   = useState<number | null>(null)
  const [studyMode, setStudyMode] = useState<'experimental' | 'data_science' | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab,     setTab]     = useState<'learn' | 'hypothesis' | 'tool' | 'journal' | 'review'>('hypothesis')

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (vasLoading) return
      if (!viewerStudentId) { navigate('/'); return }
      const { data: projects } = await supabase
        .from('project').select('*').eq('student_id', viewerStudentId).eq('status', 'active')
        .order('created_at', { ascending: false }).limit(1)
      const proj = projects?.[0] ?? null
      if (cancelled) return
      if (!proj) { setLoading(false); return }
      setProject(proj as Project)

      const [{ data: rq }, { data: summary }, { data: bg }, { data: hd }, { data: sp }] = await Promise.all([
        supabase.from('research_question').select('track_committed, draft_payload').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('step1_summary').select('confirmed_topic').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('background_research').select('payload').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('hypothesis_design').select('status, mentor_note, payload, mentor_section_comments, study_mode').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('student_profile').select('grade').eq('student_id', viewerStudentId).maybeSingle(),
      ])
      if (cancelled) return
      const trackVal = ((rq as any)?.track_committed as Track) ?? 'scientific_method'
      setTrack(trackVal)
      setTopic((summary as any)?.confirmed_topic ?? '')
      setGrade(((sp as any)?.grade as number | null) ?? null)
      const bgPay = ((bg as any)?.payload ?? {}) as Record<string, any>
      setBackground(bgPay)
      setPurpose((bgPay.purpose ?? '').trim())
      setStatus(((hd as any)?.status as Status) ?? 'draft')
      setStudyMode(((hd as any)?.study_mode as 'experimental' | 'data_science' | null) ?? null)
      setMentorNote((hd as any)?.mentor_note ?? null)
      setSectionComments(((hd as any)?.mentor_section_comments ?? {}) as Record<string, string>)

      // Seed: prefer the saved Step 4 payload; else carry forward Step 2 draft.
      // Scientific migrates any pre-block payload into the block shape; engineering
      // carries the Step 2 problem_statement/user_context+need into the eng shape.
      const dp    = ((rq as any)?.draft_payload ?? {}) as Record<string, any>
      const saved = (hd as any)?.payload as (Payload & EngPayload) | null
      if (trackVal === 'engineering_design') {
        const usersNeeds = [dp.user_context, dp.need].map((s: any) => (s ?? '').trim()).filter(Boolean).join('\n\n')
        setSeed(toEngPayload(saved, { problem: dp.problem_statement, users_needs: usersNeeds }))
      } else {
        setSeed(toBlockPayload(saved, { question: dp.question, iv: dp.iv, dv: dp.dv }))
      }
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [session, viewerStudentId, vasLoading, navigate])

  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
  if (!project) return <Box sx={{ p: 3 }}><Alert severity="info">No active project found.</Alert></Box>

  const currentStep = project.current_step ?? 1
  if (currentStep < 4) {
    return (
      <Box sx={{ p: 3, maxWidth: 640 }}>
        <Alert severity="info" icon={<LockOutlinedIcon />}>
          <strong>Step 4 unlocks after your mentor approves Step 3.</strong> Finish Background Research first.
        </Alert>
      </Box>
    )
  }
  if (track === 'engineering_design') {
    return (
      <EngineeringStep4
        projectId={project.project_id}
        studentId={viewerStudentId!}
        topic={topic}
        purpose={purpose}
        background={background}
        seed={(seed as EngPayload) ?? {}}
        status={status}
        mentorNote={mentorNote}
        sectionComments={sectionComments}
        editable={viewerIsStudent}
        onStatus={(s, note) => { setStatus(s); if (note !== undefined) setMentorNote(note) }}
      />
    )
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Step 4 — Formulate Your Hypothesis</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Turn your research question and variables into a testable experiment with a clear, falsifiable hypothesis.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Learn"            value="learn" />
        <Tab label="Hypothesis"       value="hypothesis" />
        <Tab label={studyMode === 'data_science' ? 'Model Prototype' : 'Measurement Tool'} value="tool" />
        <Tab label="Journal"          value="journal" />
        <Tab label="Mentor Review"    value="review" />
      </Tabs>

      {tab === 'learn' && <LearnTab />}
      {tab === 'hypothesis' && (
        <StudyModeFork projectId={project.project_id} studentId={viewerStudentId!} phase="commit" editable={viewerIsStudent} />
      )}
      {/* Kept mounted across ALL tabs (hidden when off hypothesis/tool) so the
          student's in-progress hypothesis/tool input is never unmounted and
          re-seeded from a stale payload on tab switch. */}
      <Box sx={{ display: (tab === 'hypothesis' || tab === 'tool') ? 'block' : 'none' }}>
        <HypothesisWork
          projectId={project.project_id}
          studentId={viewerStudentId!}
          topic={topic}
          purpose={purpose}
          background={background}
          grade={grade}
          track={track}
          seed={(seed as Payload) ?? {}}
          status={status}
          mentorNote={mentorNote}
          editable={viewerIsStudent}
          view={tab === 'tool' ? 'tool' : 'hypothesis'}
          studyMode={studyMode}
          onStatus={(s, note) => { setStatus(s); if (note !== undefined) setMentorNote(note) }}
          onGoToTool={() => setTab('tool')}
        />
      </Box>
      {tab === 'journal' && (
        <JournalTab projectId={project.project_id} studentId={viewerStudentId!} />
      )}
      {tab === 'review' && (
        <MentorReviewTab status={status} mentorNote={mentorNote} sectionComments={sectionComments} />
      )}
    </Box>
  )
}

// SRC form categories → student-friendly label + the ISEF/OCSEF form number.
const FORM_META: Record<FormKey, { label: string; form: string }> = {
  human:      { label: 'Human participants',        form: 'Form 4' },
  vertebrate: { label: 'Vertebrate animals',        form: 'Form 5A' },
  biological: { label: 'Biological agents',         form: 'Form 6A' },
  tissue:     { label: 'Tissue, blood, or fluids',  form: 'Form 6B' },
  hazard:     { label: 'Hazardous materials',       form: 'Form 3' },
}

// ── SRC safety-review flag for a DV (informational; not a gate) ─────────────────
// Shows only when the study trips a pre-approval category. Non-alarming: names
// the form(s), and offers keep-the-study vs adjust (lead: computational pivot).
function FormsCallout({ forms }: { forms: FormsFlag }) {
  const triggered = (forms.triggered ?? []).filter(k => FORM_META[k])
  if (triggered.length === 0) return null
  return (
    <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mt: 1, mb: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Heads up — this may need a science-fair safety review
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
        {triggered.map(k => (
          <Chip key={k} size="small" variant="outlined" color="info"
            label={`${FORM_META[k].label} · ${FORM_META[k].form}`} />
        ))}
      </Box>
      {forms.note && <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.5 }}>{forms.note}</Typography>}
      {forms.keep && (
        <Box sx={{ mb: forms.adjust ? 1 : 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>If you keep this study</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{forms.keep}</Typography>
        </Box>
      )}
      {forms.adjust && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Or adjust to skip pre-approval</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{forms.adjust}</Typography>
        </Box>
      )}
    </Alert>
  )
}

// ── Feedback rendering (mirrors Step 2's FeedbackBlock) ─────────────────────────
function FeedbackBlock({ feedback, stale }: { feedback: AIEval; stale: boolean }) {
  const color = (s: string) => s === 'ok' ? 'success.main' : s === 'caution' ? 'warning.main' : 'error.main'
  const bg    = (s: string) => s === 'ok' ? 'rgba(46,125,50,0.08)' : s === 'caution' ? 'rgba(237,108,2,0.08)' : 'rgba(198,40,40,0.08)'
  const glyph = (s: string) => s === 'ok' ? '✓' : s === 'caution' ? '⚠' : '?'
  const dims: Array<{ key: 'shape' | 'anchor'; label: string }> = [
    { key: 'shape', label: 'Shape' }, { key: 'anchor', label: 'Anchor' },
  ]
  return (
    <Box sx={{ mt: 1.5, opacity: stale ? 0.55 : 1 }}>
      {dims.map(d => {
        const dim = feedback[d.key]; if (!dim) return null
        return (
          <Box key={d.key} sx={{ display: 'flex', gap: 1, p: 1.25, mb: 0.75, borderRadius: 1, bgcolor: bg(dim.state), border: '1px solid', borderColor: `${color(dim.state)}40` }}>
            <Typography sx={{ color: color(dim.state), fontWeight: 700, minWidth: 18, textAlign: 'center' }}>{glyph(dim.state)}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: color(dim.state), display: 'block' }}>{d.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{dim.note}</Typography>
            </Box>
          </Box>
        )
      })}
      {feedback.forms && <FormsCallout forms={feedback.forms} />}
      {feedback.ai_disclaimer && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>ⓘ {feedback.ai_disclaimer}</Typography>
      )}
    </Box>
  )
}

// ── A labeled text input (no feedback of its own) ───────────────────────────────
function LabeledField({ label, helper, value, onChange, multiline, rows, disabled }: {
  label: string; helper?: string; value: string; onChange: (v: string) => void
  multiline?: boolean; rows?: number; disabled?: boolean
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      <TextField
        fullWidth multiline={multiline} minRows={rows ?? (multiline ? 2 : 1)}
        value={value} onChange={e => onChange(e.target.value)}
        helperText={helper} disabled={disabled}
      />
    </Box>
  )
}

// ── One holistic "Check my …" button + its feedback, for a whole concept ────────
function GroupFeedback({ name, canEvaluate, evaluating, feedback, stale, disabled, onEvaluate }: {
  name: string; canEvaluate: boolean; evaluating: boolean
  feedback?: AIEval; stale: boolean; disabled?: boolean; onEvaluate: () => void
}) {
  return (
    <Box sx={{ mb: 1 }}>
      {!disabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={onEvaluate} disabled={evaluating || !canEvaluate}>
            {evaluating ? <><CircularProgress size={14} sx={{ mr: 1 }} />Checking…</> : feedback ? `Re-check my ${name}` : `Check my ${name}`}
          </Button>
          {stale && feedback && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Changed — re-check?</Typography>}
        </Box>
      )}
      {feedback && <FeedbackBlock feedback={feedback} stale={stale} />}
    </Box>
  )
}

// ── Read-only Step 3 background research, to ground the rationale ────────────────
function BackgroundReference({ bg }: { bg: Record<string, any> }) {
  const [open, setOpen] = useState(false)
  // Step 3 payload fields vary in shape: some are strings, the source papers
  // are {title,url,abstract} objects. Coerce each safely before rendering.
  const asText = (v: any): string =>
    typeof v === 'string' ? v : Array.isArray(v) ? v.filter(x => typeof x === 'string').join(', ') : ''
  const asPaper = (v: any): string =>
    v && typeof v === 'object'
      ? [typeof v.title === 'string' ? v.title : '', typeof v.url === 'string' ? v.url : ''].filter(Boolean).join(' — ')
      : ''
  const rows: Array<{ label: string; body: string }> = [
    { label: 'Purpose',                 body: asText(bg.purpose) },
    { label: 'The gap',                 body: asText(bg.the_gap) },
    { label: 'What the research shows', body: asText(bg.what_research_shows) },
    { label: 'Key principles',          body: asText(bg.principles) },
    { label: 'Review paper',            body: asPaper(bg.review_paper) },
    { label: 'Model paper',             body: asPaper(bg.model_paper) },
  ].filter(r => r.body.trim())
  if (rows.length === 0) return null
  return (
    <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(15,43,76,0.03)' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Your Step 3 background research (reference)</Typography>
          <Button size="small" onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</Button>
        </Box>
        {open && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              This is locked from Step 3. Draw on it to justify your rationale — pull the specific findings you need into the supporting-background box below.
            </Typography>
            {rows.map(r => (
              <Box key={r.label} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{r.label}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{r.body}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

// ── The work surface (Hypothesis + Measurement Tool tabs share state) ───────────
function HypothesisWork({
  projectId, studentId, topic, purpose, background, grade, track, seed, status, mentorNote, editable, view, studyMode, onStatus, onGoToTool,
}: {
  projectId: string; studentId: string; topic: string; purpose: string
  background: Record<string, any>; grade: number | null; track: Track
  seed: Payload; status: Status; mentorNote: string | null; editable: boolean
  view: 'hypothesis' | 'tool'
  studyMode: 'experimental' | 'data_science' | null
  onStatus: (s: Status, note?: string | null) => void
  onGoToTool: () => void
}) {
  const isDataScience = studyMode === 'data_science'
  const [question, setQuestion] = useState(seed.question ?? '')
  const [iv,       setIv]       = useState(seed.iv ?? '')
  const [cv,       setCv]       = useState(seed.cv ?? '')
  const [blocks,   setBlocks]   = useState<Block[]>(seed.blocks?.length ? seed.blocks : [emptyBlock()])
  const [ml,       setMl]       = useState<MlPrototype>(seed.ml_prototype ?? {})
  const [feedback, setFeedback] = useState<Record<string, AIEval>>(seed.ai_feedback ?? {})
  const [judge,        setJudge]        = useState<JudgeReadinessResult | null>(seed.judge_readiness ?? null)
  const [judgeLoading, setJudgeLoading] = useState(false)
  const [staleSet, setStaleSet] = useState<Set<string>>(new Set())
  const [evaluating, setEvaluating] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const firstRun = useRef(true)

  const isReadOnly  = status !== 'draft' || !editable
  const isReturning = status === 'draft' && !!mentorNote

  const keyFor = (group: Group, idx?: number) =>
    BLOCK_GROUPS.includes(group) ? `${group}:${idx ?? 0}` : group

  function payload(): Payload {
    return { question, iv, cv, blocks, ml_prototype: ml, ai_feedback: feedback, judge_readiness: judge ?? undefined }
  }
  const updateMl = (patch: Partial<MlPrototype>) => setMl(prev => ({ ...prev, ...patch }))

  async function save(extra?: Partial<Payload>) {
    if (isReadOnly) return
    setSaveState('saving')
    const { error: upErr } = await supabase.from('hypothesis_design').upsert({
      project_id: projectId, student_id: studentId, track,
      payload: { ...payload(), ...extra }, updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id' })
    setSaveState(upErr ? 'idle' : 'saved')
  }

  // Debounced autosave on any change (blocks compared by value).
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    if (isReadOnly) return
    const t = setTimeout(() => { save() }, 900)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, iv, cv, JSON.stringify(blocks), JSON.stringify(ml)])

  function markStale(key: string) {
    setStaleSet(prev => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n })
  }

  function updateBlock(i: number, patch: Partial<Block>) {
    setBlocks(prev => prev.map((b, idx) => idx === i ? { ...b, ...patch } : b))
  }
  function addBlock() {
    setBlocks(prev => prev.length >= 3 ? prev : [...prev, emptyBlock()])
  }
  // Removing a block shifts higher indices down; reindex block-keyed feedback/stale.
  function removeBlock(i: number) {
    setBlocks(prev => prev.filter((_, idx) => idx !== i))
    const reindex = <T,>(entries: Array<[string, T]>): Array<[string, T]> => {
      const out: Array<[string, T]> = []
      for (const [k, v] of entries) {
        const [g, idxStr] = k.split(':')
        if (idxStr === undefined) { out.push([k, v]); continue }
        const idx = Number(idxStr)
        if (idx === i) continue
        out.push([idx < i ? k : `${g}:${idx - 1}`, v])
      }
      return out
    }
    setFeedback(prev => Object.fromEntries(reindex(Object.entries(prev))))
    setStaleSet(prev => new Set(reindex(Array.from(prev).map(k => [k, true] as [string, true])).map(([k]) => k)))
  }

  async function evaluate(group: Group, idx?: number) {
    if (evaluating) return
    const key = keyFor(group, idx)
    setEvaluating(key); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step4-evaluate-field', {
        body: { project_id: projectId, student_id: studentId, group, block_index: idx ?? 0, payload: payload() },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      const next = { ...feedback, [key]: data.evaluation as AIEval }
      setFeedback(next)
      setStaleSet(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n })
      await save({ ai_feedback: next })
    } catch (e: any) {
      setError(e.message ?? 'Could not get AI feedback — please try again.')
    } finally {
      setEvaluating(null)
    }
  }

  // "Judge-readiness" — holistic winning-bar coverage of the whole hypothesis
  // against questions judges commonly ask (step4-judge-readiness).
  async function runJudgeReadiness() {
    if (judgeLoading) return
    setJudgeLoading(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step4-judge-readiness', {
        body: { project_id: projectId, student_id: studentId, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const result = data.readiness as JudgeReadinessResult
      setJudge(result)
      await save({ judge_readiness: result })
    } catch (e: any) {
      setError(e.message ?? 'Could not run the check — please try again.')
    } finally {
      setJudgeLoading(false)
    }
  }

  const blockNonEmpty = (b: Block) =>
    [b.dv, b.dv_measurement, b.hypothesis, b.rationale, b.supporting_background, b.tool].some(s => (s ?? '').trim())
  // Readiness: a rough "on-target" % derived from the AI checks the student has
  // run (ok=1, caution=0.7, thinking=0.2; unchecked=0). Reaching 60% means most
  // concepts have been checked and are mostly ok/caution. Empty extra hypotheses
  // don't count. This is the submit gate — we block low-effort, not imperfection.
  const conceptPts = (d?: Dim) => d?.state === 'ok' ? 1 : d?.state === 'caution' ? 0.7 : d?.state === 'thinking' ? 0.2 : 0
  const readinessKeys: string[] = ['question', 'iv', 'cv']
  blocks.forEach((b, i) => {
    if (i > 0 && !blockNonEmpty(b)) return
    readinessKeys.push(`dv:${i}`, `hypothesis:${i}`)
    if (b.needs_tool && !isDataScience) readinessKeys.push(`tool:${i}`)
  })
  const readinessPct = Math.round(
    (readinessKeys.reduce((s, k) => {
      const f = feedback[k]
      return s + (f ? (conceptPts(f.shape) + conceptPts(f.anchor)) / 2 : 0)
    }, 0) / readinessKeys.length) * 100,
  )
  // A hypothesis that flagged "build a tool" but hasn't described it yet — the
  // student must finish that on the Measurement Tool tab before submitting.
  // In data-science mode the ML model IS the apparatus (checked via mlIncomplete),
  // so a leftover needs_tool flag must NOT gate submission or hide the submit
  // button behind "Work on Measurement Tool".
  const toolIncomplete = !isDataScience && blocks.some((b, i) => (i === 0 || blockNonEmpty(b)) && b.needs_tool && (b.tool ?? '').trim().length === 0)
  // Data-science: the model IS the apparatus — require its key technicals
  // (dataset, model, package) instead of a physical measurement tool.
  const mlIncomplete = isDataScience && !((ml.dataset ?? '').trim() && (ml.model_name ?? '').trim() && (ml.package ?? '').trim())
  const hypothesisChecked = !!feedback['hypothesis:0']
  const canSubmit = readinessPct >= 60 && hypothesisChecked && (isDataScience ? !mlIncomplete : !toolIncomplete)

  async function submit() {
    if (submitting || !canSubmit) return
    setSubmitting(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step4-submit', {
        body: { project_id: projectId, payload: payload(), track },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      onStatus('submitted', null)
    } catch (e: any) {
      setError(e.message ?? 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const fb = (key: string) => feedback[key]
  const st = (key: string) => staleSet.has(key)
  const ev = (key: string) => evaluating === key

  // Hypothesis-form guidance: suggest If-Then-Because for the grades taught it
  // (≤ 8); older students see the range of valid forms without one being pushed.
  const hypHelper = grade != null && grade <= 8
    ? "For your grade, a clear way to write it is If-Then-Because: 'if [your independent variable] changes, then [your dependent variable] will change, because [your reason].' A comparative ('A will differ from B') or threshold ('the effect peaks at…') form works too."
    : "Make it specific, testable, and falsifiable. Pick the shape that fits your prediction — If-Then-Because ('if [IV]…, then [DV]…, because [reason]'), comparative ('A will differ from B'), or threshold ('the effect peaks at…')."

  // DV group for block i — rendered up top (in the IV→DV→CV trio) for the
  // primary hypothesis, and inside the card for any additional hypothesis.
  const dvGroup = (i: number) => {
    const b = blocks[i]
    const derived = b.dv_kind === 'derived'
    const build = !!b.needs_tool
    return (
      <>
        <LabeledField label="Dependent variable — the outcome your hypothesis is about"
          helper="The outcome you expect to change — e.g. 'force', 'reaction time', 'plant growth'. (Not necessarily what you put an instrument on — see below.)"
          value={b.dv ?? ''} onChange={v => { updateBlock(i, { dv: v }); markStale(keyFor('dv', i)) }}
          disabled={isReadOnly} />

        {/* Two structural choices first, then a detail field tailored to both. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>How will you get a number for it?</Typography>
          <Tooltip title="Direct — read it straight off one instrument (e.g. height on a ruler). Calculate — combine measurements with a formula (e.g. force = mass × acceleration).">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <ToggleButtonGroup
          exclusive size="small" value={b.dv_kind ?? 'direct'} disabled={isReadOnly} sx={{ mb: 1.5 }}
          onChange={(_, v) => { if (v) { updateBlock(i, { dv_kind: v as 'direct' | 'derived' }); markStale(keyFor('dv', i)) } }}
        >
          <ToggleButton value="direct">I measure it directly</ToggleButton>
          <ToggleButton value="derived">I calculate it from measurements</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Do you have a tool for it, or will you build one?</Typography>
          <Tooltip title="Build/configure — a device, sensor, app, or program you set up yourself, vs. a ready-made tool like a ruler, scale, or timer.">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <ToggleButtonGroup
          exclusive size="small" value={build ? 'build' : 'standard'} disabled={isReadOnly} sx={{ mb: build ? 0.5 : 1.5 }}
          onChange={(_, v) => { if (v) { updateBlock(i, { needs_tool: v === 'build' }); markStale(keyFor('dv', i)) } }}
        >
          <ToggleButton value="standard">A ready-made tool</ToggleButton>
          <ToggleButton value="build">I'll build or configure one</ToggleButton>
        </ToggleButtonGroup>
        {build && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Describe it on the <strong>Measurement Tool</strong> tab — required before you submit.
          </Typography>
        )}

        {/* 'What to measure' lives here for every direct DV — including a built
            tool, where HOW it's built is the separate Measurement Tool tab. */}
        {!derived && (
          <LabeledField
            label={build ? 'What will you measure? (output + units)' : 'How will you measure it? (tool + units)'}
            helper={build
              ? "What your device will measure and the units — e.g. 'grip force in newtons'. You'll describe how you build the device on the Measurement Tool tab."
              : "Name the tool and the units — e.g. 'height in cm with a ruler'. (Exact model/precision comes in the next step.)"}
            value={b.dv_measurement ?? ''} onChange={v => { updateBlock(i, { dv_measurement: v }); markStale(keyFor('dv', i)) }}
            multiline disabled={isReadOnly} />
        )}
        {derived && (
          <>
            <LabeledField label="What will you actually measure? (each with units)"
              helper={build
                ? "The quantities your built tool will read — e.g. 'mass in kg', 'acceleration in m/s²'."
                : "The quantities you put an instrument on — e.g. 'mass in kg (scale)', 'acceleration in m/s² (motion sensor)'."}
              value={b.measured_quantities ?? ''} onChange={v => { updateBlock(i, { measured_quantities: v }); markStale(keyFor('dv', i)) }}
              multiline disabled={isReadOnly} />
            <LabeledField label={`How do you calculate ${b.dv?.trim() || 'it'} from those? (formula)`}
              helper="The formula that turns your measurements into your dependent variable — e.g. 'force = mass × acceleration'."
              value={b.formula ?? ''} onChange={v => { updateBlock(i, { formula: v }); markStale(keyFor('dv', i)) }}
              disabled={isReadOnly} />
          </>
        )}

        {/* Plain-English recap of the two choices, so the combination is visible */}
        {(b.dv ?? '').trim() && (
          <Box sx={{ mb: 1.5, px: 1.25, py: 0.75, bgcolor: 'rgba(15,43,76,0.05)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">→ {dvSummary(b)}</Typography>
          </Box>
        )}

        <GroupFeedback name="dependent variable" disabled={isReadOnly}
          canEvaluate={(b.dv ?? '').trim().length > 1} evaluating={ev(keyFor('dv', i))}
          feedback={fb(keyFor('dv', i))} stale={st(keyFor('dv', i))} onEvaluate={() => evaluate('dv', i)} />
      </>
    )
  }

  // Hypothesis group for block i — hypothesis + rationale, with supporting
  // background revealed only when the AI finds the Step 3 research falls short.
  const hypGroup = (i: number) => {
    const b = blocks[i]
    const hypFb = fb(keyFor('hypothesis', i))
    const showSupport = hypFb?.needs_background === true || (b.supporting_background ?? '').trim().length > 0
    return (
      <>
        <LabeledField label="Hypothesis — your testable prediction"
          helper={hypHelper}
          value={b.hypothesis ?? ''} onChange={v => { updateBlock(i, { hypothesis: v }); markStale(keyFor('hypothesis', i)) }}
          multiline rows={2} disabled={isReadOnly} />
        <LabeledField label="Rationale — the “because” of your hypothesis"
          helper="Why do you expect this? Explain the reasoning that leads from what you know to your prediction — this is the 'because' part."
          value={b.rationale ?? ''} onChange={v => { updateBlock(i, { rationale: v }); markStale(keyFor('hypothesis', i)) }}
          multiline rows={3} disabled={isReadOnly} />
        {showSupport && (
          <LabeledField label="Supporting background — add the evidence your rationale needs"
            helper="Your Step 3 research doesn't fully cover this yet — add the specific findings and principles (with citations) that back your rationale, doing more research if needed. The AI will check these against your rationale."
            value={b.supporting_background ?? ''} onChange={v => { updateBlock(i, { supporting_background: v }); markStale(keyFor('hypothesis', i)) }}
            multiline rows={3} disabled={isReadOnly} />
        )}
        <GroupFeedback name="hypothesis & grounding" disabled={isReadOnly}
          canEvaluate={(b.hypothesis ?? '').trim().length > 8} evaluating={ev(keyFor('hypothesis', i))}
          feedback={fb(keyFor('hypothesis', i))} stale={st(keyFor('hypothesis', i))} onEvaluate={() => evaluate('hypothesis', i)} />
      </>
    )
  }

  // ── Model Prototype tab (data-science) — the model is the apparatus ─────────
  if (view === 'tool' && isDataScience) {
    return (
      <Box sx={{ maxWidth: 760 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Your model prototype</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          For a data-science project your model is the apparatus — the tool that turns your dataset into results.
          Note the key technical choices below. You build and run the model itself in the <strong>AI Research Lab</strong>;
          here we just capture what you used.
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <LabeledField
              label="Dataset(s)"
              helper="What data are you modeling? Name it and where it's from (e.g. 'Pima Indians Diabetes dataset, Kaggle'), and what one row represents."
              value={ml.dataset ?? ''} onChange={v => updateMl({ dataset: v })}
              multiline rows={3} disabled={isReadOnly} />
            <LabeledField
              label="Model"
              helper="The model / algorithm you're using — e.g. 'Logistic Regression', 'Random Forest', 'a small CNN'."
              value={ml.model_name ?? ''} onChange={v => updateMl({ model_name: v })}
              disabled={isReadOnly} />
            <LabeledField
              label="Package / library"
              helper="What you build it with — e.g. scikit-learn, Keras / TensorFlow, PyTorch."
              value={ml.package ?? ''} onChange={v => updateMl({ package: v })}
              disabled={isReadOnly} />
            <LabeledField
              label="Code link (optional)"
              helper="Share your Colab / notebook if you'd like — not required."
              value={ml.colab_url ?? ''} onChange={v => updateMl({ colab_url: v })}
              disabled={isReadOnly} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  // ── Measurement Tool tab — one section per DV block that flagged a build ─────
  if (view === 'tool') {
    const toolBlocks = blocks.map((b, i) => ({ b, i })).filter(x => x.b.needs_tool)
    return (
      <Box sx={{ maxWidth: 760 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Building a way to measure</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Not every project needs a custom tool. If a standard instrument works (ruler, scale, timer, thermometer, a phone app), you're set.
          A tool section appears here for each dependent variable where you flagged <em>"I need to build or configure something to measure this"</em> on the Hypothesis tab. That can be a physical device, a sensor rig, software, computer vision, or an AI tool.
        </Typography>
        {toolBlocks.length === 0 ? (
          <Alert severity="success" variant="outlined">
            No measurement tool needed yet — a standard instrument covers your measurements. If that changes, flag it under a dependent variable on the Hypothesis tab.
          </Alert>
        ) : toolBlocks.map(({ b, i }) => (
          <Card key={i} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {blocks.length > 1 ? `Hypothesis ${i + 1} · ` : ''}Measuring {b.dv?.trim() || 'your dependent variable'}
              </Typography>
              <LabeledField
                label="Describe the tool you'll build or configure"
                helper="What it does, the key parts (or the software/model), and how it takes the measurement — enough for us to check it's good enough."
                value={b.tool ?? ''} onChange={v => { updateBlock(i, { tool: v }); markStale(keyFor('tool', i)) }}
                multiline rows={4} disabled={isReadOnly}
              />
              <GroupFeedback name="measurement tool" disabled={isReadOnly}
                canEvaluate={(b.tool ?? '').trim().length > 3} evaluating={ev(keyFor('tool', i))}
                feedback={fb(keyFor('tool', i))} stale={st(keyFor('tool', i))}
                onEvaluate={() => evaluate('tool', i)} />
            </CardContent>
          </Card>
        ))}
      </Box>
    )
  }

  // ── Hypothesis tab ──────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 760 }}>
      {isReturning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Your mentor sent this back:</strong>
          {mentorNote && <Box sx={{ mt: 0.5 }}><Markdown>{mentorNote}</Markdown></Box>}
        </Alert>
      )}
      {status === 'submitted' && <Alert severity="info" sx={{ mb: 2 }}>Submitted to your mentor for review.</Alert>}
      {(status === 'approved_draft' || status === 'completed') && (
        <Alert severity="success" sx={{ mb: 2 }}>Approved by your mentor — Step 5 is unlocked.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {(topic || purpose) && (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(15,43,76,0.03)' }}>
          <CardContent sx={{ py: 1.5 }}>
            {topic && <Typography variant="body2"><strong>Topic:</strong> {topic}</Typography>}
            {purpose && <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Your purpose (Step 3):</strong> {purpose}</Typography>}
          </CardContent>
        </Card>
      )}

      {/* Research question (light standalone check) */}
      <LabeledField label="Research question" helper="Your variables should be visible in it."
        value={question} onChange={v => { setQuestion(v); markStale('question') }} multiline disabled={isReadOnly} />
      <GroupFeedback name="research question" disabled={isReadOnly}
        canEvaluate={question.trim().length > 5} evaluating={ev('question')}
        feedback={fb('question')} stale={st('question')} onEvaluate={() => evaluate('question')} />

      <Divider sx={{ my: 3 }} />

      {/* IV group */}
      <LabeledField label="Independent variable — what will you change, and how?"
        helper="Change just one factor. Name it and how you'll set it up — the groups you'll compare, or the levels/amounts you'll test (with units). If a second thing also matters, add it as another hypothesis below rather than a second factor."
        value={iv} onChange={v => { setIv(v); markStale('iv') }} multiline rows={3} disabled={isReadOnly} />
      <GroupFeedback name="independent variable" disabled={isReadOnly}
        canEvaluate={iv.trim().length > 1} evaluating={ev('iv')}
        feedback={fb('iv')} stale={st('iv')} onEvaluate={() => evaluate('iv')} />

      <Divider sx={{ my: 3 }} />

      {/* DV group (primary hypothesis) — kept in the IV → DV → CV order */}
      {dvGroup(0)}

      <Divider sx={{ my: 3 }} />

      {/* CV group */}
      <LabeledField label="Controlled variables — what will you keep constant, and why?"
        helper="List the other things that could affect your result and must stay the same across every trial, and why each one matters. (Exactly how you'll hold them constant comes in the next step.)"
        value={cv} onChange={v => { setCv(v); markStale('cv') }} multiline rows={3} disabled={isReadOnly} />
      <GroupFeedback name="controlled variables" disabled={isReadOnly}
        canEvaluate={cv.trim().length > 1} evaluating={ev('cv')}
        feedback={fb('cv')} stale={st('cv')} onEvaluate={() => evaluate('cv')} />

      <Divider sx={{ my: 3 }} />

      <BackgroundReference bg={background} />

      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>Your {blocks.length > 1 ? 'hypotheses' : 'hypothesis'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        Now state your prediction using the variables above — a testable claim about how they relate. There's more than one valid way to write it.
        {blocks.length === 1 ? ' Add another hypothesis only if you\'re genuinely testing a second, distinct outcome.' : ''}
      </Typography>

      {blocks.map((b, i) => (
        <Card key={i} variant="outlined" sx={{ mb: 2.5 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {blocks.length === 1 ? 'Hypothesis' : `Hypothesis ${i + 1}`}
              </Typography>
              {!isReadOnly && blocks.length > 1 && (
                <Button size="small" color="inherit" onClick={() => removeBlock(i)}>Remove</Button>
              )}
            </Box>

            {/* Primary hypothesis uses the DV defined above; additional
                hypotheses bring their own new outcome. */}
            {i === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                About your dependent variable{b.dv?.trim() ? `: ${b.dv.trim()}` : ' above'}.
              </Typography>
            ) : (
              <>
                {dvGroup(i)}
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {hypGroup(i)}
          </CardContent>
        </Card>
      ))}

      {!isReadOnly && blocks.length < 3 && (
        <Button size="small" onClick={addBlock} sx={{ mb: 2 }}>+ Add another hypothesis</Button>
      )}

      <JudgeReadinessPanel
        description="This looks at your whole hypothesis together — variables, measurement, and prediction — and scores how much of it could stand up to the questions science-fair judges commonly ask — so you can strengthen your plan (not memorize answers)."
        subject="hypothesis"
        canRun={!isReadOnly && (blocks[0]?.hypothesis ?? '').trim().length > 8}
        hint={!isReadOnly && (blocks[0]?.hypothesis ?? '').trim().length <= 8 ? 'Write your hypothesis first.' : null}
        loading={judgeLoading}
        onRun={runJudgeReadiness}
        result={judge}
      />

      {!isReadOnly && (
        toolIncomplete ? (
          // A build was flagged — finish the tool before the hypothesis can go out.
          <Box sx={{ mt: 3 }}>
            <Button variant="contained" onClick={onGoToTool}>Work on Measurement Tool</Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              You chose to build or configure a measurement tool — describe it on the Measurement Tool tab, then come back to submit.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, maxWidth: 380 }}>
              <LinearProgress
                variant="determinate" value={Math.min(readinessPct, 100)}
                color={readinessPct >= 60 ? 'success' : 'warning'}
                sx={{ flex: 1, height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92 }}>
                {readinessPct}% · need 60%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="contained" onClick={submit} disabled={!canSubmit || submitting}>
                {submitting ? <><CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />Submitting…</> : 'Submit to mentor'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
              </Typography>
            </Box>
            {!canSubmit && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {!hypothesisChecked
                  ? 'Run “Check my hypothesis & grounding” on your hypothesis, and use the AI checks to reach 60%.'
                  : 'Run the AI checks and address the flags to reach 60%.'}
              </Typography>
            )}
          </Box>
        )
      )}
    </Box>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Engineering Design track — mirrors the scientific page: Learn · Requirements ·
// Solution · Journal · Mentor Review. Same per-concept AI feedback, autosave,
// readiness gate, and submit flow (step4-submit) as HypothesisWork.
// ════════════════════════════════════════════════════════════════════════════

function EngineeringStep4({
  projectId, studentId, topic, purpose, background, seed, status, mentorNote, sectionComments, editable, onStatus,
}: {
  projectId: string; studentId: string; topic: string; purpose: string
  background: Record<string, any>; seed: EngPayload; status: Status
  mentorNote: string | null; sectionComments: Record<string, string>; editable: boolean
  onStatus: (s: Status, note?: string | null) => void
}) {
  const [tab, setTab] = useState<'learn' | 'requirements' | 'solution' | 'journal' | 'review'>('requirements')
  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Step 4 — Design Requirements</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Turn your problem into a design: name who it serves, what existing solutions miss, and the measurable requirements your solution must meet.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Learn"         value="learn" />
        <Tab label="Requirements"  value="requirements" />
        <Tab label="Solution"      value="solution" />
        <Tab label="Journal"       value="journal" />
        <Tab label="Mentor Review" value="review" />
      </Tabs>

      {tab === 'learn' && <EngLearnTab />}
      {/* Kept mounted across ALL tabs (hidden when off requirements/solution) so
          in-progress engineering input is never unmounted and re-seeded from a
          stale payload on tab switch. */}
      <Box sx={{ display: (tab === 'requirements' || tab === 'solution') ? 'block' : 'none' }}>
        <EngineeringWork
          projectId={projectId}
          studentId={studentId}
          topic={topic}
          purpose={purpose}
          background={background}
          seed={seed}
          status={status}
          mentorNote={mentorNote}
          editable={editable}
          view={tab === 'solution' ? 'solution' : 'requirements'}
          onStatus={onStatus}
        />
      </Box>
      {tab === 'journal' && (
        <JournalTab projectId={projectId} studentId={studentId} />
      )}
      {tab === 'review' && (
        <MentorReviewTab status={status} mentorNote={mentorNote} sectionComments={sectionComments} />
      )}
    </Box>
  )
}

// ── The engineering work surface (Requirements + Solution tabs share state) ─────
function EngineeringWork({
  projectId, studentId, topic, purpose, background, seed, status, mentorNote, editable, view, onStatus,
}: {
  projectId: string; studentId: string; topic: string; purpose: string
  background: Record<string, any>; seed: EngPayload; status: Status
  mentorNote: string | null; editable: boolean
  view: 'requirements' | 'solution'
  onStatus: (s: Status, note?: string | null) => void
}) {
  const [problem,    setProblem]    = useState(seed.problem ?? '')
  const [forHuman,   setForHuman]   = useState(seed.for_human_user !== false)
  const [usersNeeds, setUsersNeeds] = useState(seed.users_needs ?? '')
  const [perfGoal,   setPerfGoal]   = useState(seed.performance_goal ?? '')
  const [priorArt,   setPriorArt]   = useState(seed.prior_art ?? '')
  const [blocks,     setBlocks]     = useState<EngBlock[]>(seed.requirements?.length ? seed.requirements : [emptyEngBlock()])
  const [mvp,        setMvp]        = useState(seed.mvp ?? '')
  const [tools,      setTools]      = useState(seed.tools ?? '')
  const [solution,   setSolution]   = useState(seed.solution ?? '')
  const [feedback,   setFeedback]   = useState<Record<string, AIEval>>(seed.ai_feedback ?? {})
  const [judge,        setJudge]        = useState<JudgeReadinessResult | null>(seed.judge_readiness ?? null)
  const [judgeLoading, setJudgeLoading] = useState(false)
  const [staleSet,   setStaleSet]   = useState<Set<string>>(new Set())
  const [evaluating, setEvaluating] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)
  const [saveState,  setSaveState]  = useState<'idle' | 'saving' | 'saved'>('idle')
  const firstRun = useRef(true)

  const isReadOnly  = status !== 'draft' || !editable
  const isReturning = status === 'draft' && !!mentorNote

  const keyFor = (group: Group, idx?: number) =>
    BLOCK_GROUPS.includes(group) ? `${group}:${idx ?? 0}` : group

  function payload(): EngPayload {
    return {
      problem, for_human_user: forHuman, users_needs: usersNeeds, performance_goal: perfGoal,
      prior_art: priorArt, requirements: blocks, mvp, tools, solution, ai_feedback: feedback,
      judge_readiness: judge ?? undefined,
    }
  }

  async function save(extra?: Partial<EngPayload>) {
    if (isReadOnly) return
    setSaveState('saving')
    const { error: upErr } = await supabase.from('hypothesis_design').upsert({
      project_id: projectId, student_id: studentId, track: 'engineering_design',
      payload: { ...payload(), ...extra }, updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id' })
    setSaveState(upErr ? 'idle' : 'saved')
  }

  // Debounced autosave on any change (blocks compared by value).
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    if (isReadOnly) return
    const t = setTimeout(() => { save() }, 900)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem, forHuman, usersNeeds, perfGoal, priorArt, mvp, tools, solution, JSON.stringify(blocks)])

  function markStale(key: string) {
    setStaleSet(prev => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n })
  }

  function updateBlock(i: number, patch: Partial<EngBlock>) {
    setBlocks(prev => prev.map((b, idx) => idx === i ? { ...b, ...patch } : b))
  }
  function addBlock() {
    setBlocks(prev => prev.length >= 5 ? prev : [...prev, emptyEngBlock()])
  }
  // Removing a block shifts higher indices down; reindex block-keyed feedback/stale.
  function removeBlock(i: number) {
    setBlocks(prev => prev.filter((_, idx) => idx !== i))
    const reindex = <T,>(entries: Array<[string, T]>): Array<[string, T]> => {
      const out: Array<[string, T]> = []
      for (const [k, v] of entries) {
        const [g, idxStr] = k.split(':')
        if (idxStr === undefined) { out.push([k, v]); continue }
        const idx = Number(idxStr)
        if (idx === i) continue
        out.push([idx < i ? k : `${g}:${idx - 1}`, v])
      }
      return out
    }
    setFeedback(prev => Object.fromEntries(reindex(Object.entries(prev))))
    setStaleSet(prev => new Set(reindex(Array.from(prev).map(k => [k, true] as [string, true])).map(([k]) => k)))
  }

  async function evaluate(group: Group, idx?: number) {
    if (evaluating) return
    const key = keyFor(group, idx)
    setEvaluating(key); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step4-evaluate-field', {
        body: { project_id: projectId, student_id: studentId, group, block_index: idx ?? 0, payload: payload() },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      const next = { ...feedback, [key]: data.evaluation as AIEval }
      setFeedback(next)
      setStaleSet(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n })
      await save({ ai_feedback: next })
    } catch (e: any) {
      setError(e.message ?? 'Could not get AI feedback — please try again.')
    } finally {
      setEvaluating(null)
    }
  }

  // "Judge-readiness" — holistic winning-bar coverage of the whole set of design
  // requirements against questions judges commonly ask (step4-judge-readiness).
  async function runJudgeReadiness() {
    if (judgeLoading) return
    setJudgeLoading(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step4-judge-readiness', {
        body: { project_id: projectId, student_id: studentId, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const result = data.readiness as JudgeReadinessResult
      setJudge(result)
      await save({ judge_readiness: result })
    } catch (e: any) {
      setError(e.message ?? 'Could not run the check — please try again.')
    } finally {
      setJudgeLoading(false)
    }
  }
  const engJudgeReady = problem.trim().length > 5 && (blocks[0]?.requirement ?? '').trim().length > 1

  const blockNonEmpty = (b: EngBlock) => [b.requirement, b.criterion].some(s => (s ?? '').trim())
  // Readiness: same "on-target" % as the scientific track (ok=1, caution=0.7,
  // thinking=0.2; unchecked=0). Reaching 60% means most concepts are checked and
  // mostly ok/caution. Empty extra requirements don't count. This is the submit
  // gate — we block low-effort, not imperfection.
  const conceptPts = (d?: Dim) => d?.state === 'ok' ? 1 : d?.state === 'caution' ? 0.7 : d?.state === 'thinking' ? 0.2 : 0
  const readinessKeys: string[] = ['problem', 'audience', 'prior_art', 'solution']
  blocks.forEach((b, i) => {
    if (i > 0 && !blockNonEmpty(b)) return
    readinessKeys.push(`requirement:${i}`)
  })
  const readinessPct = Math.round(
    (readinessKeys.reduce((s, k) => {
      const f = feedback[k]
      return s + (f ? (conceptPts(f.shape) + conceptPts(f.anchor)) / 2 : 0)
    }, 0) / readinessKeys.length) * 100,
  )
  const requirementChecked = !!feedback['requirement:0']
  const canSubmit = readinessPct >= 60 && requirementChecked

  async function submit() {
    if (submitting || !canSubmit) return
    setSubmitting(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step4-submit', {
        body: { project_id: projectId, payload: payload(), track: 'engineering_design' },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      onStatus('submitted', null)
    } catch (e: any) {
      setError(e.message ?? 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const fb = (key: string) => feedback[key]
  const st = (key: string) => staleSet.has(key)
  const ev = (key: string) => evaluating === key

  const audienceText = forHuman ? usersNeeds : perfGoal

  // ── Solution tab — the shared MVP + tools + solution concept ─────────────────
  if (view === 'solution') {
    return (
      <Box sx={{ maxWidth: 760 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Your solution</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Sketch the solution you'll actually build. Start with the smallest version that meets the need — the Minimum Viable Product — then list the tools and materials, and describe how it works.
        </Typography>
        <LabeledField label="MVP — the Minimum Viable Product"
          helper="The smallest build that satisfies the need — what's the least you can make that still solves the problem?"
          value={mvp} onChange={v => { setMvp(v); markStale('solution') }} multiline rows={3} disabled={isReadOnly} />
        <LabeledField label="Tools & materials"
          helper="The parts, materials, software, or equipment you'll use to build it."
          value={tools} onChange={v => { setTools(v); markStale('solution') }} multiline rows={2} disabled={isReadOnly} />
        <LabeledField label="Solution description"
          helper="How your solution works and how it meets your design requirements."
          value={solution} onChange={v => { setSolution(v); markStale('solution') }} multiline rows={4} disabled={isReadOnly} />
        <GroupFeedback name="solution" disabled={isReadOnly}
          canEvaluate={[mvp, tools, solution].some(s => s.trim().length > 1)} evaluating={ev('solution')}
          feedback={fb('solution')} stale={st('solution')} onEvaluate={() => evaluate('solution')} />
      </Box>
    )
  }

  // ── Requirements tab ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 760 }}>
      {isReturning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Your mentor sent this back:</strong>
          {mentorNote && <Box sx={{ mt: 0.5 }}><Markdown>{mentorNote}</Markdown></Box>}
        </Alert>
      )}
      {status === 'submitted' && <Alert severity="info" sx={{ mb: 2 }}>Submitted to your mentor for review.</Alert>}
      {(status === 'approved_draft' || status === 'completed') && (
        <Alert severity="success" sx={{ mb: 2 }}>Approved by your mentor — Step 5 is unlocked.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {(topic || purpose) && (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(15,43,76,0.03)' }}>
          <CardContent sx={{ py: 1.5 }}>
            {topic && <Typography variant="body2"><strong>Topic:</strong> {topic}</Typography>}
            {purpose && <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Your purpose (Step 3):</strong> {purpose}</Typography>}
          </CardContent>
        </Card>
      )}

      {/* Problem */}
      <LabeledField label="The problem — what will your design solve?"
        helper="A specific, real problem — a concrete need or pain point, not a vague wish. Carried from Step 2; refine it here."
        value={problem} onChange={v => { setProblem(v); markStale('problem') }} multiline rows={3} disabled={isReadOnly} />
      <GroupFeedback name="problem" disabled={isReadOnly}
        canEvaluate={problem.trim().length > 5} evaluating={ev('problem')}
        feedback={fb('problem')} stale={st('problem')} onEvaluate={() => evaluate('problem')} />

      <Divider sx={{ my: 3 }} />

      {/* Human user? → users & needs OR beneficiary & performance goal */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Is your project for a human user?</Typography>
        <Tooltip title="Most designs serve people (a user with needs). Some serve a performance goal instead — e.g. removing microplastics from water — with no direct human user.">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <ToggleButtonGroup
        exclusive size="small" value={forHuman ? 'yes' : 'no'} disabled={isReadOnly} sx={{ mb: 1.5 }}
        onChange={(_, v) => { if (v) { setForHuman(v === 'yes'); markStale('audience') } }}
      >
        <ToggleButton value="yes">Yes — it's for people</ToggleButton>
        <ToggleButton value="no">No — it's a performance goal</ToggleButton>
      </ToggleButtonGroup>

      {forHuman ? (
        <LabeledField label="Users & needs — who are your users, why, and what do they need?"
          helper="Name your specific target users (not 'everyone'), why they're the ones affected, and their 1–3 concrete needs."
          value={usersNeeds} onChange={v => { setUsersNeeds(v); markStale('audience') }} multiline rows={3} disabled={isReadOnly} />
      ) : (
        <LabeledField label="Beneficiary & performance goal — who or what benefits, and the concrete goal?"
          helper="Name what benefits (a system, environment, or process) and the concrete, measurable performance goal — e.g. '≥90% microplastic removal'."
          value={perfGoal} onChange={v => { setPerfGoal(v); markStale('audience') }} multiline rows={3} disabled={isReadOnly} />
      )}
      <GroupFeedback name={forHuman ? 'users & needs' : 'beneficiary & goal'} disabled={isReadOnly}
        canEvaluate={audienceText.trim().length > 1} evaluating={ev('audience')}
        feedback={fb('audience')} stale={st('audience')} onEvaluate={() => evaluate('audience')} />

      <Divider sx={{ my: 3 }} />

      {/* Prior art & gaps — grounded in Step 3 research */}
      <LabeledField label="Prior art & gaps — what already exists, and what do they miss?"
        helper="Name 3 existing solutions and 3 specific gaps they leave. Ground the gaps in your Step 3 research below."
        value={priorArt} onChange={v => { setPriorArt(v); markStale('prior_art') }} multiline rows={4} disabled={isReadOnly} />
      <BackgroundReference bg={background} />
      <GroupFeedback name="prior art & gaps" disabled={isReadOnly}
        canEvaluate={priorArt.trim().length > 5} evaluating={ev('prior_art')}
        feedback={fb('prior_art')} stale={st('prior_art')} onEvaluate={() => evaluate('prior_art')} />

      <Divider sx={{ my: 3 }} />

      {/* Design requirements — repeatable requirement ↔ criterion pairs */}
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>Design requirements</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        A requirement is a measurable spec your solution must meet; its performance criterion is the test that proves you met it. Start with your one most important requirement — add more only for genuinely distinct specs.
      </Typography>

      {blocks.map((b, i) => (
        <Card key={i} variant="outlined" sx={{ mb: 2.5 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {blocks.length === 1 ? 'Requirement' : `Requirement ${i + 1}`}
              </Typography>
              {!isReadOnly && blocks.length > 1 && (
                <Button size="small" color="inherit" onClick={() => removeBlock(i)}>Remove</Button>
              )}
            </Box>
            <LabeledField label="Requirement — a measurable, testable spec"
              helper="A concrete spec your solution must meet — e.g. 'detects objects within 50 cm'. Avoid vague words like 'works well'."
              value={b.requirement ?? ''} onChange={v => { updateBlock(i, { requirement: v }); markStale(keyFor('requirement', i)) }}
              multiline rows={2} disabled={isReadOnly} />
            <LabeledField label="Performance criterion — how you'll validate it works"
              helper="The test plus a clear pass bar — e.g. 'over 10 trials, flags 9/10 objects at 40–50 cm'. Not just a restatement of the requirement."
              value={b.criterion ?? ''} onChange={v => { updateBlock(i, { criterion: v }); markStale(keyFor('requirement', i)) }}
              multiline rows={2} disabled={isReadOnly} />
            <GroupFeedback name="requirement" disabled={isReadOnly}
              canEvaluate={(b.requirement ?? '').trim().length > 1} evaluating={ev(keyFor('requirement', i))}
              feedback={fb(keyFor('requirement', i))} stale={st(keyFor('requirement', i))}
              onEvaluate={() => evaluate('requirement', i)} />
          </CardContent>
        </Card>
      ))}

      {!isReadOnly && blocks.length < 5 && (
        <Button size="small" onClick={addBlock} sx={{ mb: 2 }}>+ Add another requirement</Button>
      )}

      <JudgeReadinessPanel
        description="This looks at your whole design plan together — problem, users, existing solutions, and requirements — and scores how much of it could stand up to the questions science-fair judges commonly ask — so you can strengthen your design (not memorize answers)."
        subject="design requirements"
        canRun={!isReadOnly && engJudgeReady}
        hint={!isReadOnly && !engJudgeReady ? 'Write your problem and at least one requirement first.' : null}
        loading={judgeLoading}
        onRun={runJudgeReadiness}
        result={judge}
      />

      {!isReadOnly && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, maxWidth: 380 }}>
            <LinearProgress
              variant="determinate" value={Math.min(readinessPct, 100)}
              color={readinessPct >= 60 ? 'success' : 'warning'}
              sx={{ flex: 1, height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92 }}>
              {readinessPct}% · need 60%
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" onClick={submit} disabled={!canSubmit || submitting}>
              {submitting ? <><CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />Submitting…</> : 'Submit to mentor'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
            </Typography>
          </Box>
          {!canSubmit && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {!requirementChecked
                ? 'Run “Check my requirement” on your primary requirement, and use the AI checks to reach 60%.'
                : 'Run the AI checks and address the flags to reach 60%.'}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

// ── Learn tab — lesson-5 teaching videos (Scientific track) ─────────────────────
// Same UX as the Step 1–3 Learn tabs: numbered sections with an intro, and
// collapsed video cards that expand one at a time (embed mounts on demand).

const LEARN_SECTIONS = [
  {
    title: 'Hypothesis vs. design requirements',
    intro: 'Scientific and engineering projects formalize their direction differently. This lesson shows what a testable experimental hypothesis is — and how it differs from the design requirements an engineering project would write instead.',
    videos: [
      { title: 'Experimental Hypothesis vs. Engineering Requirements', videoId: '857685803' },
    ],
  },
  {
    title: 'Writing your hypothesis',
    intro: 'A hypothesis is a testable prediction about how your variables relate — and there\'s more than one valid way to write it. These two lessons walk through a 5-step process for getting from your variables to a falsifiable hypothesis.',
    videos: [
      { title: 'Writing a Hypothesis — The 5-Step Process (Part 1)', videoId: '857685837' },
      { title: 'Writing a Hypothesis — The 5-Step Process (Part 2)', videoId: '857685872' },
    ],
  },
]

function VimeoEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      <iframe
        title={title}
        src={`https://player.vimeo.com/video/${videoId}?title=0&portrait=0&byline=0`}
        width="640"
        height="360"
        style={{ border: 'none', maxWidth: '100%' }}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    </Box>
  )
}

function LearnTab() {
  const [openVideo, setOpenVideo] = useState<string | null>(null)
  return (
    <Box sx={{ maxWidth: 760, py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        Watch these short lessons before writing your hypothesis. They cover what makes a prediction
        testable, and a step-by-step process for writing one from the variables you defined in Step 2.
      </Typography>
      {LEARN_SECTIONS.map((section, si) => (
        <Box key={si} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {si + 1}. {section.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            {section.intro}
          </Typography>
          {section.videos.map(v => (
            <Card key={v.videoId} variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => setOpenVideo(openVideo === v.videoId ? null : v.videoId)}
                >
                  <PlayCircleOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{v.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {openVideo === v.videoId ? 'Hide' : 'Watch'}
                  </Typography>
                </Box>
                {openVideo === v.videoId && <VimeoEmbed videoId={v.videoId} title={v.title} />}
              </CardContent>
            </Card>
          ))}
          {si < LEARN_SECTIONS.length - 1 && <Divider sx={{ mt: 2.5 }} />}
        </Box>
      ))}
    </Box>
  )
}

// ── Learn tab — lesson-5 teaching videos (Engineering Design track) ─────────────
const ENG_LEARN_SECTIONS = [
  {
    title: 'Hypothesis vs. design requirements',
    intro: 'Scientific and engineering projects formalize their direction differently. This lesson shows what design requirements are for an engineering project — and how they differ from the testable hypothesis a scientific project would write instead.',
    videos: [
      { title: 'Experimental Hypothesis vs. Engineering Requirements', videoId: '857685803' },
    ],
  },
  {
    title: 'Writing design requirements',
    intro: 'A design requirement is a measurable, testable spec your solution must meet — paired with a performance criterion that proves you met it. This lesson walks through writing requirements that are specific enough to build and test.',
    videos: [
      { title: 'Write Design Requirements', videoId: '857685970' },
    ],
  },
]

function EngLearnTab() {
  const [openVideo, setOpenVideo] = useState<string | null>(null)
  return (
    <Box sx={{ maxWidth: 760, py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        Watch these short lessons before writing your design requirements. They cover how an engineering
        project formalizes its direction, and how to write requirements that are specific and testable.
      </Typography>
      {ENG_LEARN_SECTIONS.map((section, si) => (
        <Box key={si} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {si + 1}. {section.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            {section.intro}
          </Typography>
          {section.videos.map(v => (
            <Card key={v.videoId} variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => setOpenVideo(openVideo === v.videoId ? null : v.videoId)}
                >
                  <PlayCircleOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{v.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {openVideo === v.videoId ? 'Hide' : 'Watch'}
                  </Typography>
                </Box>
                {openVideo === v.videoId && <VimeoEmbed videoId={v.videoId} title={v.title} />}
              </CardContent>
            </Card>
          ))}
          {si < ENG_LEARN_SECTIONS.length - 1 && <Divider sx={{ mt: 2.5 }} />}
        </Box>
      ))}
    </Box>
  )
}

// ── Mentor Review tab (student-facing status) ───────────────────────────────────
const S4_SECTION_LABELS: Record<string, string> = {
  // scientific
  question: 'Research question', iv: 'Independent variable', cv: 'Controlled variables',
  dv: 'Dependent variable(s)', hypothesis: 'Hypothesis & grounding', tool: 'Measurement tool',
  // engineering
  problem: 'Problem', audience: 'Users & needs', prior_art: 'Prior art & gaps',
  requirement: 'Design requirements', solution: 'Solution',
}

function MentorReviewTab({ status, mentorNote, sectionComments }: {
  status: Status; mentorNote: string | null; sectionComments: Record<string, string>
}) {
  if (status === 'submitted') return <Alert severity="info">Your work is with your mentor for review. You'll see their decision on your dashboard.</Alert>
  const sections = Object.entries(sectionComments).filter(([, v]) => (v ?? '').trim())
  if (status === 'approved_draft' || status === 'completed') {
    return (
      <Alert severity="success">
        <strong>Approved by your mentor — Step 5 is unlocked.</strong>
        {mentorNote && <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{mentorNote}</Box>}
        {sections.map(([key, comment]) => (
          <Box key={key} sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{S4_SECTION_LABELS[key] ?? key}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment}</Typography>
          </Box>
        ))}
      </Alert>
    )
  }
  if (mentorNote || sections.length > 0) {
    return (
      <Alert severity="warning">
        <strong>Your mentor sent this back with feedback:</strong>
        {mentorNote && <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{mentorNote}</Box>}
        {sections.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {sections.map(([key, comment]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{S4_SECTION_LABELS[key] ?? key}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment}</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Revise your work and resubmit.</Typography>
      </Alert>
    )
  }
  return <Alert severity="info">Finish your hypothesis and submit it for mentor review from the Hypothesis tab.</Alert>
}
